import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PriModal from "../PriModal";
import TableSkeleton from "../../loaders/TableSkeleton";
import TeacherAttendanceService from "../../../services/TeacherAttendanceService";
import "../../../styles/TeacherAttendance.css";

const STATUSES = ["PRESENT", "ABSENT"];

const EMPTY_FORM = {
  teacherId: "",
  attendanceDate: "",
  checkInTime: "",
  checkOutTime: "",
  status: "PRESENT",
};

function getErrorMessage(error, t) {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message.trim();
  }
  if (typeof responseData?.error === "string" && responseData.error.trim()) {
    return responseData.error.trim();
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  return t("teacherAttendanceRequestFailed");
}

function displayDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return "—";
  }
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function displayTime(value) {
  if (typeof value !== "string") {
    return "—";
  }
  const time = value.trim();
  const twelveHourTime = time.match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?\s*(AM|PM)$/i);
  if (twelveHourTime) {
    const hour = Number(twelveHourTime[1]);
    if (hour < 1 || hour > 12) return "—";
    return `${String(hour).padStart(2, "0")}:${twelveHourTime[2]} ${twelveHourTime[3].toUpperCase()}`;
  }
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(time)) {
    return "—";
  }
  return time.slice(0, 5);
}

function getTeacherName(teacher) {
  if (typeof teacher?.name === "string" && teacher.name.trim()) {
    return teacher.name.trim();
  }
  return "—";
}

function getTeacherDisplayName(teacher) {
  const name = getTeacherName(teacher);
  const id = teacher?.id;
  return name === "—" || id == null ? name : `${name} (#${id})`;
}

function getToday() {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function getAcademicYearLabel(value, t) {
  if (typeof value === "string" && value.trim() && value !== "N/A") {
    return value.trim();
  }
  if (value && typeof value === "object") {
    const year = value.year || value.currentYear;
    if (typeof year === "string" && year.trim()) return year.trim();
  }
  return t("teacherAttendanceCurrentAcademicYear");
}

function timeForInput(value) {
  if (typeof value !== "string") return "";
  const time = value.trim();
  const twelveHourTime = time.match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?\s*(AM|PM)$/i);
  if (twelveHourTime) {
    const hour = Number(twelveHourTime[1]);
    if (hour < 1 || hour > 12) return "";
    const hour24 = (hour % 12) + (twelveHourTime[3].toUpperCase() === "PM" ? 12 : 0);
    return `${String(hour24).padStart(2, "0")}:${twelveHourTime[2]}`;
  }
  return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(time)
    ? time.slice(0, 5)
    : "";
}

function toApiTime(value) {
  return value ? `${value}:00` : null;
}

function TeacherAttendance({
  schoolId,
  academicYearId,
  academicYearName,
  teachers = [],
  onToast,
}) {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [loadError, setLoadError] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterMode, setFilterMode] = useState("date");
  const [filterDate, setFilterDate] = useState(getToday);
  const [filterMonth, setFilterMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const requestId = useRef(0);

  const schoolReady = schoolId !== undefined && schoolId !== null && schoolId !== "";
  const academicYearReady =
    academicYearId !== undefined && academicYearId !== null && academicYearId !== "";
  const busy = Boolean(busyAction);
  const academicYearLabel = getAcademicYearLabel(academicYearName, t);
  const availableTeachers = useMemo(
    () =>
      Array.isArray(teachers)
        ? teachers.filter((teacher) => teacher?.active === true && teacher.id != null)
        : [],
    [teachers]
  );
  const activeTeacherIds = useMemo(
    () => new Set(availableTeachers.map((teacher) => String(teacher.id))),
    [availableTeachers]
  );
  const activeTeacherRecords = useMemo(
    () =>
      records.filter((record) => {
        const teacherId = record?.teacher?.id ?? record?.teacherId;
        return teacherId != null && activeTeacherIds.has(String(teacherId));
      }),
    [activeTeacherIds, records]
  );
  const dateFilteredRecords = useMemo(
    () =>
      filterMode !== "date"
        ? activeTeacherRecords
        : activeTeacherRecords.filter(
            (record) => String(record.attendanceDate ?? "").slice(0, 10) === filterDate
          ),
    [activeTeacherRecords, filterDate, filterMode]
  );
  const filteredRecords = useMemo(
    () =>
      statusFilter === "all"
        ? dateFilteredRecords
        : dateFilteredRecords.filter((record) => record.status === statusFilter.toUpperCase()),
    [dateFilteredRecords, statusFilter]
  );
  const presentCount = dateFilteredRecords.filter((record) => record.status === "PRESENT").length;
  const absentCount = dateFilteredRecords.filter((record) => record.status === "ABSENT").length;

  const notify = useCallback(
    (message, type) => {
      if (typeof onToast === "function") {
        onToast(message, type);
      }
    },
    [onToast]
  );

  const loadSchoolAttendance = useCallback(async () => {
    if (!schoolReady || !academicYearReady) {
      setRecords([]);
      setLoadError(t("teacherAttendanceAccountNotConfigured"));
      return false;
    }

    const currentRequestId = ++requestId.current;
    setLoading(true);
    setLoadError("");
    try {
      const data = await TeacherAttendanceService.getBySchool(schoolId, academicYearId);
      if (currentRequestId !== requestId.current) return false;
      if (!Array.isArray(data)) {
        throw new Error(t("teacherAttendanceUnexpectedResponse"));
      }
      setRecords(data);
      return true;
    } catch (error) {
      if (currentRequestId !== requestId.current) return false;
      const message = getErrorMessage(error, t);
      setLoadError(message);
      notify(message, "error");
      return false;
    } finally {
      if (currentRequestId === requestId.current) {
        setLoading(false);
      }
    }
  }, [academicYearId, academicYearReady, notify, schoolId, schoolReady, t]);

  useEffect(() => {
    loadSchoolAttendance();
    return () => {
      requestId.current += 1;
    };
  }, [loadSchoolAttendance]);

  useEffect(() => {
    if (
      teacherFilter &&
      !availableTeachers.some((teacher) => String(teacher.id) === String(teacherFilter))
    ) {
      setTeacherFilter("");
    }
  }, [availableTeachers, teacherFilter]);

  const applyFilters = async (event) => {
    event.preventDefault();
    if (!schoolReady) {
      setLoadError(t("teacherAttendanceAccountNotConfigured"));
      return;
    }

    const teacherId = teacherFilter;
    if (!teacherId && (filterMode === "month" || filterMode === "range")) {
      notify(t("teacherAttendanceSelectTeacherForDateFilter"), "error");
      return;
    }
    if (!teacherId && !academicYearReady) {
      setLoadError(t("teacherAttendanceAccountNotConfigured"));
      return;
    }
    if (teacherId && filterMode === "none" && !academicYearReady) {
      setLoadError(t("teacherAttendanceAccountNotConfigured"));
      return;
    }
    if (filterMode === "date" && !filterDate) {
      notify(t("teacherAttendanceSelectDate"), "error");
      return;
    }
    if (filterMode === "month" && !filterMonth) {
      notify(t("teacherAttendanceSelectMonth"), "error");
      return;
    }
    if (filterMode === "range") {
      if (!fromDate || !toDate) {
        notify(t("teacherAttendanceSelectDateRange"), "error");
        return;
      }
      if (fromDate > toDate) {
        notify(t("teacherAttendanceInvalidDateRange"), "error");
        return;
      }
    }

    const currentRequestId = ++requestId.current;
    setLoading(true);
    setLoadError("");
    try {
      let data;
      if (!teacherId) {
        data = await TeacherAttendanceService.getBySchool(schoolId, academicYearId);
      } else if (filterMode === "date") {
        data = await TeacherAttendanceService.getByTeacherAndDate(teacherId, filterDate);
      } else if (filterMode === "month") {
        data = await TeacherAttendanceService.getByTeacherAndMonth(teacherId, filterMonth);
      } else if (filterMode === "range") {
        data = await TeacherAttendanceService.getByTeacherAndDateRange(
          teacherId,
          fromDate,
          toDate
        );
      } else {
        data = await TeacherAttendanceService.getByTeacherAndAcademicYear(
          teacherId,
          academicYearId
        );
      }

      if (currentRequestId !== requestId.current) return;
      if (!Array.isArray(data)) {
        throw new Error(t("teacherAttendanceUnexpectedResponse"));
      }
      if (!teacherId && filterMode === "date") {
        data = data.filter(
          (record) => String(record.attendanceDate ?? "").slice(0, 10) === filterDate
        );
      }
      setRecords(data);
    } catch (error) {
      if (currentRequestId !== requestId.current) return;
      const message = getErrorMessage(error, t);
      setLoadError(message);
      notify(message, "error");
    } finally {
      if (currentRequestId === requestId.current) {
        setLoading(false);
      }
    }
  };

  const clearFilters = () => {
    setTeacherFilter("");
    setStatusFilter("all");
    setFilterMode("date");
    setFilterDate(getToday());
    setFilterMonth("");
    setFromDate("");
    setToDate("");
    loadSchoolAttendance();
  };

  const openCreateModal = () => {
    setForm({
      ...EMPTY_FORM,
      attendanceDate: getToday(),
    });
    setFormError("");
    setModal({ type: "create" });
  };

  const openEditModal = (record) => {
    setForm({
      teacherId: String(record?.teacher?.id ?? ""),
      attendanceDate:
        typeof record?.attendanceDate === "string" ? record.attendanceDate : "",
      checkInTime: timeForInput(record?.checkInTime),
      checkOutTime: timeForInput(record?.checkOutTime),
      status: STATUSES.includes(record?.status) ? record.status : "PRESENT",
    });
    setFormError("");
    setModal({ type: "edit", record });
  };

  const closeModal = () => {
    if (!busy) setModal(null);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!form.attendanceDate || !STATUSES.includes(form.status)) {
      setFormError(t("teacherAttendanceDateAndStatusRequired"));
      return;
    }

    if (modal?.type === "create" && !form.teacherId) {
      setFormError(t("teacherAttendanceSelectTeacher"));
      return;
    }

    if (!schoolReady || !academicYearReady) {
      setFormError(t("teacherAttendanceAccountNotConfigured"));
      return;
    }

    const attendanceFields = {
      attendanceDate: form.attendanceDate,
      checkInTime: toApiTime(form.checkInTime),
      checkOutTime: toApiTime(form.checkOutTime),
      status: form.status,
    };

    setBusyAction(modal.type === "create" ? "saving" : "updating");
    setFormError("");
    try {
      if (modal.type === "create") {
        await TeacherAttendanceService.createAttendance({
          school: { id: schoolId },
          academicYear: { id: academicYearId },
          teacher: { id: form.teacherId },
          ...attendanceFields,
          source: "MANUAL",
        });
        notify(t("teacherAttendanceCreated"), "success");
      } else {
        await TeacherAttendanceService.updateAttendance(modal.record.id, attendanceFields);
        notify(t("teacherAttendanceUpdated"), "success");
      }
      setModal(null);
      await loadSchoolAttendance();
    } catch (error) {
      const message = getErrorMessage(error, t);
      setFormError(message);
      notify(message, "error");
    } finally {
      setBusyAction("");
    }
  };

  const handleDelete = async () => {
    if (modal?.type !== "delete") return;
    setBusyAction("deleting");
    try {
      await TeacherAttendanceService.deleteAttendance(modal.record.id);
      notify(t("teacherAttendanceDeleted"), "success");
      setModal(null);
      await loadSchoolAttendance();
    } catch (error) {
      const message = getErrorMessage(error, t);
      setFormError(message);
      notify(message, "error");
    } finally {
      setBusyAction("");
    }
  };

  const handleRefresh = async () => {
    if (!schoolReady || !academicYearReady) {
      notify(t("teacherAttendanceAccountNotConfigured"), "error");
      return;
    }
    setBusyAction("refreshing");
    try {
      const refreshed = await loadSchoolAttendance();
      if (refreshed) {
        notify(t("teacherAttendanceRefreshCompleted"), "success");
      }
    } finally {
      setBusyAction("");
    }
  };

  const handleExportAndEmail = async () => {
    if (!schoolReady || !academicYearReady) {
      notify(t("teacherAttendanceAccountNotConfigured"), "error");
      return;
    }
    setBusyAction("exporting");
    try {
      const response = await TeacherAttendanceService.exportAndEmailAttendance(
        schoolId,
        academicYearId
      );
      notify(
        typeof response === "string" && response.trim()
          ? response
          : t("teacherAttendanceExportCompleted"),
        "success"
      );
    } catch (error) {
      notify(getErrorMessage(error, t), "error");
    } finally {
      setBusyAction("");
    }
  };

  const filterRequiresTeacher = filterMode === "month" || filterMode === "range";
  const currentTeacher = (teacherId) =>
    availableTeachers.find((teacher) => String(teacher.id) === String(teacherId));

  return (
    <main className="pri-content teacher-attendance">
      <header className="teacher-attendance-header">
        <div>
          <div className="content-heading">{t("teacherAttendance")}</div>
          <div className="content-text">
            {t("teacherAttendanceManageDaily")}
            {academicYearReady ? ` • ${academicYearLabel}` : ""}
          </div>
        </div>
        <div className="teacher-attendance-header-actions">
          <button
            className="pri-btn pri-btn-primary"
            type="button"
            onClick={openCreateModal}
            disabled={busy || !schoolReady || !academicYearReady || availableTeachers.length === 0}
          >
            + {t("teacherAttendanceAdd")}
          </button>
          <button
            className="pri-btn pri-btn-secondary"
            type="button"
            onClick={handleExportAndEmail}
            disabled={busy || !schoolReady || !academicYearReady}
          >
            {busyAction === "exporting"
              ? t("teacherAttendanceSending")
              : t("teacherAttendanceExportEmail")}
          </button>
        </div>
      </header>

      {(!schoolReady || !academicYearReady) && (
        <div className="teacher-attendance-alert" role="alert">
          {t("teacherAttendanceAccountSetupRequired")}
        </div>
      )}

      <section className="teacher-attendance-card" aria-label={t("teacherAttendanceFilters")}>
        <form onSubmit={applyFilters}>
          <div className="teacher-attendance-filters">
            <div className="teacher-attendance-field">
              <label htmlFor="teacher-attendance-year">{t("academicYear")}</label>
              <input
                id="teacher-attendance-year"
                type="text"
                value={academicYearLabel}
                readOnly
                aria-readonly="true"
              />
            </div>
            <div className="teacher-attendance-field">
              <label htmlFor="teacher-attendance-teacher">{t("teacher")}</label>
              <select
                id="teacher-attendance-teacher"
                value={teacherFilter}
                onChange={(event) => setTeacherFilter(event.target.value)}
                disabled={busy || availableTeachers.length === 0}
              >
                <option value="">{t("teacherAttendanceAllTeachers")}</option>
                {availableTeachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {getTeacherName(teacher)}
                  </option>
                ))}
              </select>
            </div>
            <div className="teacher-attendance-field">
              <label htmlFor="teacher-attendance-filter-type">{t("teacherAttendanceFilterBy")}</label>
              <select
                id="teacher-attendance-filter-type"
                value={filterMode}
                onChange={(event) => setFilterMode(event.target.value)}
                disabled={busy}
              >
                <option value="none">{t("teacherAttendanceAllDates")}</option>
                <option value="date">{t("teacherAttendanceDate")}</option>
                <option value="month">{t("teacherAttendanceMonth")}</option>
                <option value="range">{t("teacherAttendanceDateRange")}</option>
              </select>
            </div>
            {filterMode === "date" && (
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-date">{t("teacherAttendanceDateLabel")}</label>
                <input
                  id="teacher-attendance-date"
                  type="date"
                  value={filterDate}
                  onChange={(event) => setFilterDate(event.target.value)}
                  disabled={busy}
                  required
                />
              </div>
            )}
            {filterMode === "month" && (
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-month">{t("teacherAttendanceMonth")}</label>
                <input
                  id="teacher-attendance-month"
                  type="month"
                  value={filterMonth}
                  onChange={(event) => setFilterMonth(event.target.value)}
                  disabled={busy}
                  required
                />
              </div>
            )}
            {filterMode === "range" && (
              <>
                <div className="teacher-attendance-field">
                  <label htmlFor="teacher-attendance-from">{t("teacherAttendanceFromDate")}</label>
                  <input
                    id="teacher-attendance-from"
                    type="date"
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
                <div className="teacher-attendance-field">
                  <label htmlFor="teacher-attendance-to">{t("teacherAttendanceToDate")}</label>
                  <input
                    id="teacher-attendance-to"
                    type="date"
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
              </>
            )}
          </div>
          {filterRequiresTeacher && (
            <p className="teacher-attendance-filter-note">
              {t("teacherAttendanceSelectTeacherForDateFilter")}
            </p>
          )}
          <div className="teacher-attendance-filter-actions">
            <button className="pri-btn pri-btn-primary" type="submit" disabled={busy}>
              {t("teacherAttendanceApplyFilters")}
            </button>
            <button
              className="pri-btn pri-btn-ghost"
              type="button"
              onClick={clearFilters}
              disabled={busy}
            >
              {t("teacherAttendanceClearFilters")}
            </button>
          </div>
        </form>
      </section>

      <section className="teacher-attendance-card">
        <div className="teacher-attendance-toolbar">
          <div>
            <h2>{t("teacherAttendanceRecordsTitle")}</h2>
            <span>
              {loading
                ? t("teacherAttendanceLoadingRecords")
                : filteredRecords.length === 1
                  ? t("teacherAttendanceSingleRecord")
                  : t("teacherAttendanceRecordCount", { count: filteredRecords.length })}
            </span>
          </div>
          <div className="teacher-attendance-sync">
            <button
              className="pri-btn pri-btn-secondary"
              type="button"
              onClick={handleRefresh}
              disabled={busy || loading || !schoolReady || !academicYearReady}
            >
              {busyAction === "refreshing"
                ? t("teacherAttendanceRefreshing")
                : `↻ ${t("teacherAttendanceRefresh")}`}
            </button>
          </div>
        </div>

        <div className="teacher-attendance-status-filters">
          <span className="teacher-attendance-status-filter-label">
            {t("teacherAttendanceStatusFilter")}
          </span>
          <div
            className="teacher-attendance-status-options"
            role="group"
            aria-label={t("teacherAttendanceStatusFilter")}
          >
            {[
              { value: "all", label: t("teacherAttendanceAll"), count: dateFilteredRecords.length },
              { value: "present", label: t("teacherAttendanceStatusPresent"), count: presentCount },
              { value: "absent", label: t("teacherAttendanceStatusAbsent"), count: absentCount },
            ].map((option) => (
              <button
                key={option.value}
                className={`teacher-attendance-status-filter${
                  statusFilter === option.value ? " is-active" : ""
                }`}
                type="button"
                onClick={() => setStatusFilter(option.value)}
                aria-pressed={statusFilter === option.value}
                disabled={busy}
              >
                <span>{option.label}</span>
                <span className="teacher-attendance-status-count">{option.count}</span>
              </button>
            ))}
          </div>
        </div>

        {loadError ? (
          <div className="teacher-attendance-empty" role="alert">
            <p>{loadError}</p>
            <button
              className="pri-btn pri-btn-ghost"
              type="button"
              onClick={loadSchoolAttendance}
              disabled={busy || loading}
            >
              {t("retry")}
            </button>
          </div>
        ) : (
          <div className="table-wrap teacher-attendance-table-wrap">
            {loading ? (
              <TableSkeleton rows={5} columns={7} />
            ) : (
              <table className="pri-table teacher-attendance-table">
                <thead>
                  <tr>
                    <th>{t("teacher")}</th>
                    <th>{t("teacherAttendanceDateLabel")}</th>
                    <th>{t("teacherAttendanceCheckIn")}</th>
                    <th>{t("teacherAttendanceCheckOut")}</th>
                    <th>{t("status")}</th>
                    <th>{t("teacherAttendanceSource")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="pri-empty teacher-attendance-empty">
                          <div className="pri-empty-icon">📋</div>
                          <p>
                            {dateFilteredRecords.length === 0
                              ? t("teacherAttendanceNoRecords")
                              : t("teacherAttendanceNoStatusMatches")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((record, index) => (
                      <tr
                        key={record.id ?? `${record.attendanceDate}-${record.teacher?.id}-${index}`}
                        className={record.status === "ABSENT" ? "teacher-attendance-row-absent" : ""}
                      >
                        <td>{getTeacherDisplayName(record.teacher)}</td>
                        <td>{displayDate(record.attendanceDate)}</td>
                        <td>{displayTime(record.checkInTime)}</td>
                        <td>{displayTime(record.checkOutTime)}</td>
                        <td>
                          <span
                            className={`teacher-attendance-status ${
                              record.status === "PRESENT"
                                ? "teacher-attendance-status-present"
                                : record.status === "ABSENT"
                                  ? "teacher-attendance-status-absent"
                                  : ""
                            }`}
                          >
                            {record.status === "PRESENT"
                              ? t("teacherAttendanceStatusPresent")
                              : record.status === "ABSENT"
                                ? t("teacherAttendanceStatusAbsent")
                                : record.status || "—"}
                          </span>
                        </td>
                        <td>
                          {typeof record.source === "string" && record.source.trim()
                            ? record.source
                            : "—"}
                        </td>
                        <td>
                          <div className="teacher-attendance-row-actions">
                            <button
                              className="pri-btn pri-btn-ghost pri-btn-sm"
                              type="button"
                              onClick={() => openEditModal(record)}
                              disabled={busy || record.id == null}
                              aria-label={t("teacherAttendanceEditFor", {
                                teacher: getTeacherName(record.teacher),
                              })}
                            >
                              {t("edit")}
                            </button>
                            <button
                              className="pri-btn pri-btn-danger pri-btn-sm"
                              type="button"
                              onClick={() => {
                                setFormError("");
                                setModal({ type: "delete", record });
                              }}
                              disabled={busy || record.id == null}
                              aria-label={t("teacherAttendanceDeleteFor", {
                                teacher: getTeacherName(record.teacher),
                              })}
                            >
                              {t("delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {(modal?.type === "create" || modal?.type === "edit") && (
        <PriModal
          title={
            modal.type === "create"
              ? t("teacherAttendanceAddModalTitle")
              : t("teacherAttendanceEditModalTitle")
          }
          onClose={closeModal}
          wide
          footer={
            <>
              <button className="pri-btn pri-btn-ghost" type="button" onClick={closeModal} disabled={busy}>
                {t("cancel")}
              </button>
              <button
                className="pri-btn pri-btn-primary"
                type="submit"
                form="teacher-attendance-form"
                disabled={busy}
              >
                {busyAction === "saving"
                  ? t("saving")
                  : busyAction === "updating"
                    ? t("teacherAttendanceUpdating")
                    : modal.type === "create"
                      ? t("teacherAttendanceCreate")
                      : t("saveChanges")}
              </button>
            </>
          }
        >
          <form id="teacher-attendance-form" onSubmit={handleFormSubmit}>
            <div className="teacher-attendance-form-grid">
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-teacher">
                  {t("teacher")} *
                </label>
                {modal.type === "create" ? (
                  <select
                    id="teacher-attendance-form-teacher"
                    value={form.teacherId}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, teacherId: event.target.value }))
                    }
                    disabled={busy}
                    required
                  >
                    <option value="">{t("selectTeacher")}</option>
                    {availableTeachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {getTeacherDisplayName(teacher)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="teacher-attendance-form-teacher"
                    type="text"
                    value={getTeacherDisplayName(currentTeacher(form.teacherId) || modal.record.teacher)}
                    readOnly
                  />
                )}
              </div>
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-year">{t("academicYear")}</label>
                <input
                  id="teacher-attendance-form-year"
                  type="text"
                  value={academicYearLabel}
                  readOnly
                />
              </div>
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-date">
                  {t("teacherAttendanceDateLabel")} *
                </label>
                <input
                  id="teacher-attendance-form-date"
                  type="date"
                  value={form.attendanceDate}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, attendanceDate: event.target.value }))
                  }
                  disabled={busy}
                  required
                />
              </div>
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-status">{t("status")} *</label>
                <select
                  id="teacher-attendance-form-status"
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, status: event.target.value }))
                  }
                  disabled={busy}
                  required
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status === "PRESENT"
                        ? t("teacherAttendanceStatusPresent")
                        : t("teacherAttendanceStatusAbsent")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-check-in">
                  {t("teacherAttendanceCheckIn")}
                </label>
                <input
                  id="teacher-attendance-form-check-in"
                  type="time"
                  value={form.checkInTime}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, checkInTime: event.target.value }))
                  }
                  disabled={busy}
                />
              </div>
              <div className="teacher-attendance-field">
                <label htmlFor="teacher-attendance-form-check-out">
                  {t("teacherAttendanceCheckOut")}
                </label>
                <input
                  id="teacher-attendance-form-check-out"
                  type="time"
                  value={form.checkOutTime}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, checkOutTime: event.target.value }))
                  }
                  disabled={busy}
                />
              </div>
            </div>
            {formError && <p className="teacher-attendance-form-error" role="alert">{formError}</p>}
          </form>
        </PriModal>
      )}

      {modal?.type === "delete" && (
        <PriModal
          title={t("teacherAttendanceDeleteTitle")}
          onClose={closeModal}
          danger
          footer={
            <>
              <button className="pri-btn pri-btn-ghost" type="button" onClick={closeModal} disabled={busy}>
                {t("cancel")}
              </button>
              <button
                className="pri-btn pri-btn-danger"
                type="button"
                onClick={handleDelete}
                disabled={busy}
              >
                {busyAction === "deleting"
                  ? t("teacherAttendanceDeleting")
                  : t("teacherAttendanceDeleteTitle")}
              </button>
            </>
          }
        >
          <p>
            {t("teacherAttendanceDeleteConfirm", {
              teacher: getTeacherDisplayName(modal.record.teacher),
              date: displayDate(modal.record.attendanceDate),
            })}
          </p>
          {formError && <p className="teacher-attendance-form-error" role="alert">{formError}</p>}
        </PriModal>
      )}

    </main>
  );
}

export default TeacherAttendance;
